import type { RouteRecord, RouteRecordRaw } from 'vue-router';

import getFirstPermittedAdminRoute from '@/scripts/helpers/functions/permissions/getFirstPermittedAdminRoute';
import cueRoutes from '@/vue/features/cue/activity/routes';
import playlistRoutes from '@/vue/features/review/playlist/routes';
import { usePermissionsStore } from '@/vue/stores/permissions';
import adminChildren from './admin';
import notificationChildren from './notification';
import observeChildren from './observe';
import reportsRoutes from './reports';
import ROUTE_NAMES, { HOME_ROUTE_PATH } from './routeNames';
import scheduleRoutes from './schedule';

import BellIcon from '@/images/bell.svg?component';

export interface CustomRoute extends RouteRecord {
    meta: {
        title?: string,
        anonymous?: true,
        hideMenu?: true,
        permissions?: () => string[],
        deniedMessage?: string,
    }
}

const Login = () => import('../views/Login/Login.vue');
const RecordShow = () => import('../views/Review/RecordShow.vue');

const routeConfig: RouteRecordRaw[] = [
    {
        path: '/login',
        name: ROUTE_NAMES.LOGIN_MAIN,
        component: Login,
        meta: {
            title: 'Login',
            anonymous: true,
            hideMenu: true,
        },
    },
    ...cueRoutes,
    {
        path: '/login/full',
        name: ROUTE_NAMES.LOGIN_FULL,
        component: Login,
        meta: {
            title: 'Login',
            anonymous: true,
            hideMenu: true,
        },
    },
    {
        path: '/login/saml',
        name: ROUTE_NAMES.LOGIN_SSO,
        component: Login,
        meta: {
            title: 'Login with SSO',
            anonymous: true,
            hideMenu: true,
        },
    },
    {
        path: HOME_ROUTE_PATH,
        name: ROUTE_NAMES.HOME,
        component: () => import('../views/Home.vue'),
        meta: {
            title: 'Home',
            permissions: (): string[] => [usePermissionsStore().allPermissions.generalHome],
        },
    },
    {
        path: '/live',
        name: ROUTE_NAMES.OBSERVE_MAIN,
        component: () => import('../views/Observe/Observe.vue'),
        props: (route) => ({
            selectedRoomId: parseInt(route.params.roomId as string, 10) || null,
            selectedCameraId: parseInt(route.params.cameraId as string, 10) || null,
            sequence: route.params.sequence,
        }),
        meta: {
            title: 'Observe',
            permissions: (): string[] => [usePermissionsStore().allPermissions.monitoring],
        },
        children: observeChildren,
    },
    {
        path: '/record',
        name: ROUTE_NAMES.REVIEW_MAIN,
        component: () => import('../views/Review/Review.vue'),
        meta: {
            permissions: (): string[] => [usePermissionsStore().allPermissions.review],
        },
        children: [
            {
                path: 'review/list',
                name: ROUTE_NAMES.REVIEW_LIST,
                components: { list: () => import('../views/Review/ReviewList.vue') },
                meta: {
                    title: 'Review',
                    permissions: (): string[] => [usePermissionsStore().allPermissions.review],
                },
            },
            {
                path: 'review/thumbnails',
                name: ROUTE_NAMES.REVIEW_THUMBNAILS,
                components: { thumbnails: () => import('../views/Review/ReviewThumbnails.vue') },
                meta: {
                    title: 'Review',
                    permissions: (): string[] => [usePermissionsStore().allPermissions.review],
                },
            },
        ],
    },
    {
        path: '/record/:recordId',
        name: ROUTE_NAMES.REVIEW_RECORD_SHOW,
        component: RecordShow,
        meta: {
            title: 'Review',
        },
        children: [
            {
                path: 'videos/:videoId',
                name: ROUTE_NAMES.REVIEW_RECORD_SHOW_CAMERA,
                component: RecordShow,
                meta: {
                    title: 'Review',
                },
                children: [
                    {
                        path: 'comments/:commentId',
                        name: ROUTE_NAMES.REVIEW_RECORD_SHOW_CAMERA_COMMENT,
                        component: RecordShow,
                        meta: {
                            title: 'Review',
                        },
                    },
                ],
            },
            {
                path: 'comments/:commentId',
                name: ROUTE_NAMES.REVIEW_RECORD_SHOW_COMMENT,
                component: RecordShow,
                meta: {
                    title: 'Review',
                },
            },
        ],
    },
    {
        path: '/record/upload/:sequence',
        name: ROUTE_NAMES.REVIEW_UPLOAD_LINK,
        component: () => import('@/vue/features/upload-link/views/GuestUploadPage.vue'),
        meta: {
            title: 'Upload',
            anonymous: true,
        },
    },
    {
        path: '/record/share/:sequence',
        name: ROUTE_NAMES.REVIEW_RECORD_SHARED_SHOW,
        component: RecordShow,
        meta: {
            title: 'Review',
            anonymous: true,
        },
        children: [
            {
                path: 'videos/:videoId',
                name: ROUTE_NAMES.REVIEW_RECORD_SHARED_SHOW_CAMERA,
                component: RecordShow,
                meta: {
                    title: 'Review',
                    anonymous: true,
                },
                children: [
                    {
                        path: 'comments/:commentId',
                        name: ROUTE_NAMES.REVIEW_RECORD_SHARED_SHOW_CAMERA_COMMENT,
                        component: RecordShow,
                        meta: {
                            title: 'Review',
                            anonymous: true,
                        },
                    },
                ],
            },
            {
                path: 'comments/:commentId',
                name: ROUTE_NAMES.REVIEW_RECORD_SHARED_SHOW_COMMENT,
                component: RecordShow,
                meta: {
                    title: 'Review',
                    anonymous: true,
                },
            },
        ],
    },
    ...playlistRoutes,
    {
        path: '/admin',
        name: ROUTE_NAMES.ADMIN_MAIN,
        component: () => import('../views/Admin.vue'),
        redirect: (): { name: string } => (
            {
                name: getFirstPermittedAdminRoute(),
            }
        ),
        meta: {
            title: 'Admin',
            permissions: (): string[] => (
                [usePermissionsStore().allPermissions.admin]
            ),
        },
        children: adminChildren,
    },
    {
        path: '/user/edit',
        name: ROUTE_NAMES.USER_SETTINGS,
        component: () => import('../views/UserSettings.vue'),
        meta: {
            title: 'User settings',
            permissions: (): string[] => (
                [
                    usePermissionsStore().allPermissions.generalChangePassword,
                    usePermissionsStore().allPermissions.generalEditProfile,
                    usePermissionsStore().allPermissions.reviewZoom,
                ]
            ),
        },
    },
    {
        path: '/help',
        name: ROUTE_NAMES.HELP,
        component: () => import('../views/Help.vue'),
        meta: {
            title: 'Help',
            permissions: (): string[] => [usePermissionsStore().allPermissions.generalHelp],
        },
    },
    {
        path: '/no-access',
        name: ROUTE_NAMES.NO_ACCESS,
        component: () => import('../views/Root/NoAccessPage.vue'),
        meta: {
            title: 'No access',
            permissions: (): string[] => [],
        },
    },
    {
        path: '/notifications',
        name: ROUTE_NAMES.NOTIFICATIONS,
        component: () => import('../views/Notification/Notifications.vue'),
        redirect: { name: 'notificationsAll' },
        meta: {
            title: 'Notifications',
            icon: BellIcon,
            permissions: (): string[] => [],
        },
        children: notificationChildren,
    },
    ...scheduleRoutes,
    ...reportsRoutes,
    {
        path: '/logout',
        name: ROUTE_NAMES.LOGOUT,
        component: () => null,
        beforeEnter: async (): Promise<void> => {
            const { logout } = await import('../features/shared/auth/services/auth');
            await logout();
            window.location.replace('/login');
        },
        meta: {
            permissions: (): string[] => [],
        },
    },
    {
        path: '/:catchAll(.*)',
        name: ROUTE_NAMES.ERROR_PAGE,
        component: () => import('../views/Root/ErrorPage.vue'),
        meta: {
            title: 'Error',
            anonymous: true,
        },
    },
];
export default routeConfig;
